import React, { useCallback, useEffect, useState } from 'react';

import { makeStyles }  from '@material-ui/core/styles';
import Avatar          from '@material-ui/core/Avatar';
import Chip            from '@material-ui/core/Chip';
import Divider         from '@material-ui/core/Divider';
import Table           from '@material-ui/core/Table';
import TableBody       from '@material-ui/core/TableBody';
import TableCell       from '@material-ui/core/TableCell';
import TableContainer  from '@material-ui/core/TableContainer';
import TableHead       from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow        from '@material-ui/core/TableRow';

import OpenInNewIcon  from '@material-ui/icons/OpenInNew';
import FilterListIcon from '@material-ui/icons/FilterList';

import {
  create,
  date,
} from '../util';
import Attender        from './Attender';
import AttendersFilter from './AttendersFilter';
import NewAttender     from './NewAttender';

const recreate = {
  scheduleName: (schedule) => {
    if (schedule === '') {
      return  '-';
    }
    const [ day, secondsFrom, secondsTo ] = schedule.split('/');
    const HHmmFrom = date.HHmm(parseInt(secondsFrom))
    const HHmmTo = date.HHmm(parseInt(secondsTo))
    return `${'일월화수목금토'[parseInt(day)]}요일 ${HHmmFrom} - ${HHmmTo}`
  }
}

const style = {
  attenders: makeStyles((theme) => ({
    filter: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(4),
    },
    filterChips: {
      display: 'flex',
      gap: theme.spacing(2),
    },
    root: {
      flexGrow: 1,
      padding: theme.spacing(4),
    },
  })),
}

export default function Attenders(props) {
  const classes = style.attenders();

  const [ attenderCount, setAttenderCount ] = useState(0);
  const [ attenders, setAttenders ] = useState([]);
  const [ attendersFilter, setAttendersFilter ] = useState(create.attendersFilter());
  const [ page, setPage ] = useState(0);

  const {
    path,
    request,
    setNewAttender,
    setPath,
    setSnackbarMessage,
    ticketer,
  } = props.context.getStates();

  const fetchAttenders = useCallback(() => {
    request.postSecurely('/attender/select', {
      attender: -1,
      page,
      ticketer,
      ...attendersFilter,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        if ('attenders' in response) {
          setAttenders(response.attenders);
        }
        if ('count' in response) {
          setAttenderCount(response.count);
          if (page > Math.floor(response.count / 10)) {
            setPage(0);
          }
        }
      }
    });
  }, [
    attendersFilter,
    page,
    request,
    setAttenderCount,
    setPage,
    ticketer,
  ]);

  const updateAttender = useCallback((newAttender) => {
    request.postSecurely('/attender/update', {
      ...newAttender, ticketer
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        if ('attender' in response) {
          setNewAttender(create.attender);
        }
        if ('count' in response) {
          setAttenderCount(response.count);
          setPage(Math.floor(response.count / 10));
        }
        setSnackbarMessage('새 참가자를 등록하였습니다.');
        setPath('/attenders');
      }
    });
  }, [
    request,
    setNewAttender,
    setPath,
    setSnackbarMessage,
    ticketer,
  ]);

  useEffect(() => {
    setPage(0);
  }, [attendersFilter.keywords]);

  useEffect(() => {
    fetchAttenders();
  }, [
    fetchAttenders,
    path,
  ]);

  if (path.startsWith('/attenders')) {
    return (
      <div className={classes.root}>
        {(() => {
          if (path.startsWith('/attenders/attender')) {
            return (
              <Attender context={props.context} />
            );
          } else if (
            path === '/attenders' ||
            path === '/attenders#new-attender'
          ) {
            return (<>
              {path === '/attenders#new-attender' && (
                <NewAttender context={props.context}
                  updateAttender={updateAttender}
                />
              )}

              <div className={classes.filter}>
                <div className={classes.filterTerms}>
                  <AttendersFilter attendersFilter={attendersFilter}
                    setAttendersFilter={setAttendersFilter}
                  />
                </div>
                <div className={classes.filterChips}>
                  <Chip label={'참여 일정 있음'}
                    clickable
                    color="primary"
                    icon={<FilterListIcon />}
                    onClick={(event) => {
                      setAttendersFilter({...attendersFilter, attending: !attendersFilter.attending, notAttending: false});
                    }}
                    variant={attendersFilter.attending ? 'default' : 'outlined'}
                  />
                  <Chip label={'참여 일정 없음'}
                    clickable
                    color="primary"
                    icon={<FilterListIcon />}
                    onClick={(event) => {
                      setAttendersFilter({...attendersFilter, attending: false, notAttending: !attendersFilter.notAttending});
                    }}
                    variant={attendersFilter.notAttending ? 'default' : 'outlined'}
                  />
                  <Chip label={'미결제 내역 보유'}
                    clickable
                    color="primary"
                    icon={<FilterListIcon />}
                    onClick={(event) => {
                      setAttendersFilter({...attendersFilter, zeroPaid: !attendersFilter.zeroPaid});
                    }}
                    variant={attendersFilter.zeroPaid ? 'default' : 'outlined'}
                  />
                  <Chip label={'잔여 티켓 없음'}
                    clickable
                    color="primary"
                    icon={<FilterListIcon />}
                    onClick={(event) => {
                      setAttendersFilter({...attendersFilter, noUsableTickets: !attendersFilter.noUsableTickets});
                    }}
                    variant={attendersFilter.noUsableTickets ? 'default' : 'outlined'}
                  />
                </div>
              </div>

              <TablePagination page={page}
                component="div"
                count={attenderCount}
                onPageChange={(event, newPage) => { setPage(newPage); }}
                rowsPerPage={10}
                rowsPerPageOptions={[]}
              />
              <TableContainer>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      {['결제일', '마지막 참여 일시', '참여 일정', '비고'].map((column, index) => (
                        <TableCell key={index}>{column}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attenders.map((attender) => (
                      <TableRow key={attender['attender.rowid']}>
                        <TableCell variant="head" scope="row">
                          <Chip clickable variant="outlined"
                            avatar={<Avatar src={'data:image/jpeg;base64,' + attender['attender.picture64']} />}
                            deleteIcon={<OpenInNewIcon />}
                            label={attender['attender.name']}
                            onClick={() => {
                              setPath(`/attenders/attender?id=${attender['attender.rowid']}`);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <p>{date.used(attender['MAX(issued.used_at)'])}</p>
                        </TableCell>
                        <TableCell>
                          {attender['.concatenated/schedules'].split(',').sort().map((schedule, index) => (
                            <p key={index}>{recreate.scheduleName(schedule)}</p>
                          ))}
                        </TableCell>
                        <TableCell>
                          {attender['attender.birthdate'] !== 0 && (<p>
                            {`생년월일: ${date.withoutTime(attender['attender.birthdate'])}`}
                          </p>)}
                          {attender['attender.email'] !== '' && (<p>
                            {`이메일: ${attender['attender.email']}`}
                          </p>)}
                          {attender['attender.phone'] !== '' && (<p>
                            {`전화번호: ${attender['attender.phone']}`}
                          </p>)}
                          {((
                            attender['attender.birthdate'] !== 0 ||
                            attender['attender.email'] !== '' ||
                            attender['attender.phone'] !== ''
                          ) && attender['attender.note'] !== '') && (
                            <Divider />
                          )}
                          {attender['attender.note'] !== '' && (<p>
                            {attender['attender.note'].split('\n').map((line, index) => (
                              <span key={index}>{line}<br/></span>
                            ))}
                          </p>)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>);
          } else {
            return null;
          }
        })()}
      </div>
    );
  } else {
    return null;
  }
}